import { useNotification } from "hooks/useNotification";
import {
  useMutation,
  useQuery,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { CreateCustomerDTO, Customer } from "types/customer.type";
import instance from "./axiosInstance";
import { toQueryString } from "helpers/url";

export const ENDPOINT = "/customer";

export enum CustomerQueryKey {
  GetAll = "get-customers",
  GetOne = "get-customer",
  GetProxyAssignmentsForParent = "GET_CUSTOMER_PROXY_ASSIGNMENTS_AS_PARENT_KEY",
}

export const getCustomers = (params: Record<string, any>) =>
  queryOptions({
    queryKey: [CustomerQueryKey.GetAll, params],
    queryFn: async (): Promise<CustomersQueryResult> => {
      const response = await instance.get(
        `${ENDPOINT}?${toQueryString(params)}`,
      );
      return response.data;
    },
  });

export const useCustomers = (params: Record<string, any>) =>
  useQuery(getCustomers(params));

export type CustomersQueryResult = {
  customers: Customer[];
  count: number;
};
export const getCustomer = (id: number) =>
  queryOptions({
    queryKey: [CustomerQueryKey.GetOne, id],
    queryFn: async (): Promise<Customer> => {
      const response = await instance.get(`${ENDPOINT}/${id}`);
      return response.data;
    },
  });

export const useCustomer = (id: number, options = {}) =>
  useQuery({
    ...getCustomer(id),
    ...options,
  });

export const useCreateCustomer = (settings = {}) => {
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateCustomerDTO): Promise<Customer> => {
      const response = await instance.post(ENDPOINT, payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [CustomerQueryKey.GetAll],
      });
      notification.info(`${data.name} skapad`);
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    ...settings,
  });
};

export const useUpdateCustomer = (id: number) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: CreateCustomerDTO): Promise<Customer> => {
      const { emails, separators, ...rest } = payload;
      const response = await instance.put(`${ENDPOINT}/${id}`, rest);
      return response.data;
    },
    onSuccess: (data: Customer) => {
      notification.info(`Ändringarna sparades`);
      queryClient.invalidateQueries({
        queryKey: [CustomerQueryKey.GetOne, data.id],
      });
      queryClient.invalidateQueries({
        queryKey: [CustomerQueryKey.GetAll],
      });
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};

export type ProxyCustomerDto = {
  customerId: number;
  proxyCustomerId: number;
};
export const useAddProxyCustomer = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({
      customerId,
      proxyCustomerId,
    }: ProxyCustomerDto): Promise<Customer> => {
      const response = await instance.put(
        `${ENDPOINT}/${customerId}/proxy-customer`,
        {
          proxyCustomerId,
        },
      );
      return response.data;
    },
    onSuccess: (data: Customer) => {
      notification.info(`Underkund tillagd`);
      queryClient.invalidateQueries({
        queryKey: [CustomerQueryKey.GetOne, data.id],
      });
      queryClient.invalidateQueries({
        queryKey: [CustomerQueryKey.GetAll],
      });
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};

export const usePatchCustomer = (id: number, settings = {}) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (
      payload: Partial<CreateCustomerDTO>,
    ): Promise<Customer> => {
      const response = await instance.patch(`${ENDPOINT}/${id}`, payload);
      return response.data;
    },
    onSuccess: (data: Customer) => {
      notification.info(`Ändringarna sparade`);
      queryClient.invalidateQueries({
        queryKey: [CustomerQueryKey.GetOne, data.id],
      });
      queryClient.invalidateQueries({
        queryKey: [CustomerQueryKey.GetAll],
      });
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    ...settings,
  });
};

export const useDeleteCustomer = (id: number, settings = {}) => {
  return useMutation({
    mutationFn: async () => {
      return instance.delete(`${ENDPOINT}/${id}`);
    },
    ...settings,
  });
};

/**
 * Superuser tool: POST /customer/import-csv. Field name "file".
 */
export type ImportCustomersCsvRow = Record<string, string>;

export type ImportCustomersCsvRowIssue = {
  rowNumber: number;
  reason: string;
  row: ImportCustomersCsvRow;
};

export type ImportCustomersCsvResult = {
  created: number;
  updated: number;
  unchanged: number;
  ambiguousRows: ImportCustomersCsvRowIssue[];
  errorRows: ImportCustomersCsvRowIssue[];
};

export const importCustomersCsv = async (
  file: File,
): Promise<ImportCustomersCsvResult> => {
  const formData = new FormData();
  formData.append("file", file);
  return (
    await instance.post(`${ENDPOINT}/import-csv`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  ).data;
};
